/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { getDisplayNameFromProperties } from '../../../utils/getDisplayName';
import type { HydratedEntity, ViewStateLookups } from '../types';

// ============================================================================
// View State Lookups Context (for /view-state/ API)
// ============================================================================

/**
 * Context value for view state lookups.
 * Provides pre-built lookups from the /view-state/ API.
 */
interface ViewStateLookupsContextValue {
  lookups: ViewStateLookups;
  getUserDisplayName: (userId: string) => string;
  getObjectLabel: (entityId: string) => string;
  getPropertyLabel: (entityType: string, propertyName: string) => string;
  getAuditsForEvent: (eventId: string) => HydratedEntity[];
  /** Resolve an entity from the pool by entity_id. */
  getEntity: (entityId: string) => HydratedEntity | undefined;
  /** Current logged-in user's ID as string, for "You" display in timeline events */
  currentUserId?: string;
}

const ViewStateLookupsContext =
  createContext<ViewStateLookupsContextValue | null>(null);

/** Stable empty array to avoid re-renders when no audits exist for an event. */
const EMPTY_AUDITS: HydratedEntity[] = [];

/**
 * Provider for view state lookups from the /view-state/ API.
 *
 * Accepts pre-fetched lookups as a prop. Use with useTimelineViewState hook.
 *
 * @example
 * ```tsx
 * function TimelineView({ objectId, orgId }) {
 *   const { data } = useTimelineViewState({ entityId: objectId, orgId });
 *   const lookups = data?.pages ? mergeLookups(data.pages) : emptyLookups;
 *
 *   return (
 *     <ViewStateLookupsProvider lookups={lookups}>
 *       <TimelineEventList events={events} />
 *     </ViewStateLookupsProvider>
 *   );
 * }
 * ```
 */
export function ViewStateLookupsProvider({
  lookups,
  currentUserId,
  children,
}: {
  lookups: ViewStateLookups;
  currentUserId?: string;
  children: ReactNode;
}) {
  const { entities } = lookups;

  /**
   * Resolve an entity from the pool by entity_id.
   */
  const getEntity = useCallback(
    (entityId: string): HydratedEntity | undefined => entities[entityId],
    [entities]
  );

  /**
   * Get user display name by user ID.
   *
   * Resolves users[userId] → entity_id, then entities[entity_id].ahoy_display_name.
   * Returns "User" if not found or if the user is unknown (entity_id is null).
   */
  const getUserDisplayName = useCallback(
    (userId: string): string => {
      const entityId = lookups.users[userId];
      if (!entityId) {
        return 'User';
      }
      const entity = entities[entityId];
      return entity?.ahoy_display_name ?? 'User';
    },
    [lookups.users, entities]
  );

  /**
   * Get object label by entity ID.
   *
   * Looks up the entity in the entities pool and returns its display name.
   * Falls back to getDisplayNameFromProperties for entities without ahoy_display_name.
   */
  const getObjectLabel = useCallback(
    (entityId: string): string => {
      const entity = entities[entityId];
      if (!entity) {
        return 'Unknown';
      }
      return (
        entity.ahoy_display_name ??
        getDisplayNameFromProperties(entity.properties) ??
        'Unknown'
      );
    },
    [entities]
  );

  /**
   * Get property label by entity type and property name.
   * Returns the raw property name if not found.
   */
  const getPropertyLabel = useCallback(
    (entityType: string, propertyName: string): string => {
      const key = `${entityType}.${propertyName}`;
      return lookups.property_types[key]?.label || propertyName;
    },
    [lookups.property_types]
  );

  /**
   * Get audit entities for a timeline event.
   *
   * Resolves audits[eventId] → [entity_id, ...] → [HydratedEntity, ...].
   */
  const getAuditsForEvent = useCallback(
    (eventId: string): HydratedEntity[] => {
      const auditIds = lookups.audits?.[eventId];
      if (!auditIds || auditIds.length === 0) {
        return EMPTY_AUDITS;
      }
      const resolved: HydratedEntity[] = [];
      for (const auditId of auditIds) {
        const entity = entities[auditId];
        if (entity) {
          resolved.push(entity);
        }
      }
      return resolved.length > 0 ? resolved : EMPTY_AUDITS;
    },
    [lookups.audits, entities]
  );

  const value = useMemo(
    () => ({
      lookups,
      getUserDisplayName,
      getObjectLabel,
      getPropertyLabel,
      getAuditsForEvent,
      getEntity,
      currentUserId,
    }),
    [
      lookups,
      getUserDisplayName,
      getObjectLabel,
      getPropertyLabel,
      getAuditsForEvent,
      getEntity,
      currentUserId,
    ]
  );

  return (
    <ViewStateLookupsContext.Provider value={value}>
      {children}
    </ViewStateLookupsContext.Provider>
  );
}

/**
 * Hook to access view state lookups and helper functions.
 *
 * Must be used within ViewStateLookupsProvider.
 *
 * @example
 * ```tsx
 * function EventActor({ sourceId }) {
 *   const { getUserDisplayName } = useViewStateLookups();
 *   return <span>{getUserDisplayName(sourceId)}</span>;
 * }
 *
 * function AssociatedObject({ entityId }) {
 *   const { getObjectLabel } = useViewStateLookups();
 *   return <span>{getObjectLabel(entityId)}</span>;
 * }
 * ```
 */
export function useViewStateLookups(): ViewStateLookupsContextValue {
  const context = useContext(ViewStateLookupsContext);
  if (!context) {
    throw new Error(
      'useViewStateLookups must be used within ViewStateLookupsProvider'
    );
  }
  return context;
}

/**
 * Empty lookups object for use as default value.
 */
export const emptyViewStateLookups: ViewStateLookups = {
  entities: {},
  users: {},
  emails: {},
  audits: {},
  forms: {},
  property_types: {},
  association_types: {},
  events: {},
};
